// @flow

import React, {useState, useRef} from 'react';
import {Link} from 'react-router-dom';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import {DeleteOutline, Edit, InfoOutlined} from '@material-ui/icons';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../../assets/styles/colors';
import IosSwitch from '../../../../../elements/IosSwitch/IosSwitch';
import InputPreview from '../Modals/InputPreview';

import textPreview from '../../../../../assets/img/configure/text-input-preview.png';
import calendarPreview from '../../../../../assets/img/configure/calendar-input-preview.png';
// import multiOptionPreview from '../../../../../assets/img/configure/multi-option-input-preview.png';
import multiOptionPreview from '../../../../../assets/img/configure/select-input-preview.png';

import CheckoutInputPreview from './CheckoutInputPreview';

import {useBindedActions} from '../../../../../utils/globalStore';
import {useTypedSelector} from '../../../../../store';
import {INPUT_TYPE, MAP_INPUT_TYPE_NAME} from '../../../../../entities';

import {actions as CheckoutActions} from '../../duck';

import type {CheckoutInput} from '../../../../../entities';
import type {Theme} from '@material-ui/core';
import {useIsFeatureInTier} from '../../../../../utils/component';

const useStyles = props =>
  makeStyles((theme: Theme) => ({
    card: {
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      backgroundColor: props.isDragging ? '#EEE5FF' : colors.grey200,
      borderRadius: '12px',
      border: `1px solid ${props.isDragging ? '#7B39FF' : 'transparent'}`,
      padding: theme.spacing(2),
      transition: 'all 0.3s ease-in-out',
    },
    cardContent: {
      flexGrow: 1,
      background: '#FFFFFF',
      borderRadius: '12px',
      padding: theme.spacing(2),
    },
    icon: {
      background: '#EEE5FF',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      marginRight: '4px',
      padding: '4px',
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      maxWidth: '100%',
      wordBreak: 'break-word',
    },
    text: {
      fontWeight: 500,
      fontSize: 14,
      color: colors.grey900,
    },
    greyText: {
      fontWeight: 400,
      fontSize: 14,
      color: colors.grey700,
    },
    divider: {
      margin: theme.spacing(1, 0),
    },
    semiBold: {
      fontWeight: 600,
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    iconInfo: {
      color: colors.grey700,
    },
    editButton: {
      color: colors.grey700,
    },
    deleteButton: {
      color: colors.error,
    },
    previewWrap: {
      margin: theme.spacing(2, 0),
    },
    cardGroup: {
      width: '100%',
      display: 'flex',
      gap: '16px',
    },
    dragIcon: {
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
      alignItems: 'center',
      color: colors.grey600,
    },
  }));

type Props = {
  item: CheckoutInput,
  index: number,
  className?: string,
  isDragging: boolean,
  handleDragStart: (e: SyntheticDragEvent<HTMLDivElement>, item: any) => void,
  handleDragEnter: (e: SyntheticDragEvent<HTMLDivElement>, item: any) => void,
  dragItemId: string,
  extraContent?: Node,
  disabled?: boolean,
  isSaving?: boolean,
};

const actions = {
  deleteCheckoutInput: CheckoutActions.deleteCheckoutInput,
  editCheckoutInput: CheckoutActions.editCheckoutInput,
  resetCheckoutPage: CheckoutActions.resetCheckoutPage,
};

export default function CheckoutInputCard(props: Props) {
  const {
    handleDragEnter,
    handleDragStart,
    disabled,
    isDragging,
    item,
    index,
    dragItemId,
  } = props;

  const isDraggingSelf = dragItemId === item.id;

  const classes = useStyles({isDragging: isDraggingSelf})();
  const itemRef = useRef();

  const hasCustomCheckoutFeature = useIsFeatureInTier('CUSTOM_ORDER_CHECKOUT');
  const isSaving = useTypedSelector(state => state.salesProcessConfig.isSaving);

  const {
    deleteCheckoutInput,
    editCheckoutInput,
    resetCheckoutPage: resetState,
  } = useBindedActions(actions);

  const [modalType, setModalType] = useState<string | null>(null);

  const {enabled, required, inputType} = item;

  const handleCloseModal = () => setModalType(null);

  const buildModalPreview = () => {
    switch (modalType) {
      case INPUT_TYPE.TEXT:
        return (
          <InputPreview
            handleClose={handleCloseModal}
            title="Texto"
            img={textPreview}
          />
        );
      case INPUT_TYPE.NUMBER:
        return (
          <InputPreview
            handleClose={handleCloseModal}
            title="Número"
            img={textPreview}
          />
        );
      case INPUT_TYPE.DATE_PICKER:
        return (
          <InputPreview
            handleClose={handleCloseModal}
            title="Calendario"
            img={calendarPreview}
          />
        );
      case INPUT_TYPE.CHECKLIST:
        return (
          <InputPreview
            handleClose={handleCloseModal}
            title="Multi Opción"
            img={multiOptionPreview}
          />
        );
      default:
        return null;
    }
  };

  const handleDeleteInput = () => {
    deleteCheckoutInput({checkoutInputRequest: {id: item.id}});
  };

  const onStart = e => {
    if (!disabled && dragItemId !== item.id)
      handleDragStart({...e, target: itemRef.current}, item);
  };

  const onEnter = e => {
    if (!disabled && dragItemId !== item.id)
      handleDragEnter({...e, target: itemRef.current}, item);
  };

  const onRequiredChange = e => {
    const checkoutInputRequest = {
      id: item.id,
      required: e.target.checked,
    };
    editCheckoutInput({checkoutInputRequest});
  };

  const onEnabledChange = e => {
    const checkoutInputRequest = {
      id: item.id,
      enabled: e.target.checked,
    };
    editCheckoutInput({checkoutInputRequest});
  };

  return (
    <div
      ref={itemRef}
      draggable={!disabled && hasCustomCheckoutFeature}
      onDragStart={onStart}
      onDragEnter={isDragging ? e => onEnter(e) : null}
    >
      <Grid container className={classes.card}>
        {buildModalPreview()}
        <Grid className={classes.cardGroup}>
          <div className={classes.dragIcon}>
            <DragIndicatorIcon />
          </div>
          <div className={classes.cardContent}>
            <div className={classes.row}>
              <Typography className={`${classes.text} ${classes.semiBold}`}>
                Campo {index + 1}
              </Typography>
              <FormControlLabel
                disabled={isSaving || !hasCustomCheckoutFeature}
                control={
                  <IosSwitch
                    checked={enabled}
                    name="enabled"
                    onChange={onEnabledChange}
                  />
                }
                labelPlacement="start"
                label={
                  <Typography className={classes.text}>
                    {enabled ? 'Activado' : 'Desactivado'}
                  </Typography>
                }
              />
            </div>
            <Divider className={classes.divider} />
            <div className={classes.row}>
              <Typography className={classes.text}>Tipo de campo</Typography>

              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <Typography className={`${classes.text} ${classes.semiBold}`}>
                  {MAP_INPUT_TYPE_NAME[inputType] ?? ''}
                </Typography>
                <InfoOutlined
                  onClick={() => setModalType(inputType)}
                  className={classes.iconInfo}
                />
              </div>
            </div>

            <div className={classes.previewWrap}>
              <CheckoutInputPreview input={item} />
            </div>

            <div className={classes.footer}>
              <div>
                <Checkbox
                  color="primary"
                  checked={required}
                  onChange={onRequiredChange}
                  disabled={!hasCustomCheckoutFeature}
                />
                <Typography variant="span">Campo obligatorio</Typography>
              </div>
              <div className={classes.actions}>
                <CustomButton
                  className={classes.editButton}
                  startIcon={<Edit />}
                  to={`/configure/sales-process/checkout/${item.id}`}
                  component={Link}
                  disabled={isSaving || !hasCustomCheckoutFeature}
                  onClick={resetState}
                >
                  Editar
                </CustomButton>

                <CustomButton
                  className={classes.deleteButton}
                  startIcon={<DeleteOutline />}
                  onClick={handleDeleteInput}
                  disabled={isSaving || !hasCustomCheckoutFeature}
                >
                  Eliminar
                </CustomButton>
              </div>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
}
